import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Send, Instagram } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const OFFICES = [
  { city: "London", lines: ["20 Bridge Road,", "Hunton Bridge,", "Kings Langley,", "England, WD4 8RE"] },
  { city: "Manchester", lines: ["Camellia House,", "Water Lane,", "Wilmslow,", "England, SK9 5BB"] },
  { city: "Dubai", lines: ["17th Floor,", "Habtoor Business Tower,", "Dubai Marina,", "UAE"] },
];

export default function DeptContact() {
  const { dept } = useOutletContext();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  function onChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { department: dept.slug, ...form });
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="pt-20 md:pt-28 pb-28">
      <header className="text-center max-w-4xl mx-auto px-6 mb-16 md:mb-20">
        <div className="csg-label" style={{ color: dept.accent }}>
          Get In Touch
        </div>
        <h1 className="font-display uppercase leading-[0.9] mt-5 text-5xl md:text-[7vw]">
          <span className="block text-white">Get In Touch</span>
          <span className="block" style={{ color: dept.accent }}>
            With CSG.
          </span>
        </h1>
      </header>

      <div className="max-w-2xl mx-auto px-6 md:px-14">
        <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-6">
            <Field name="name" label="Full Name" value={form.name} onChange={onChange} required />
            <Field name="email" type="email" label="Email" value={form.email} onChange={onChange} required />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field name="phone" label="Phone (optional)" value={form.phone} onChange={onChange} />
            <Field name="subject" label="Subject" value={form.subject} onChange={onChange} required />
          </div>
          <div>
            <label className="csg-label text-white/60 block mb-2" style={{ color: "rgba(255,255,255,0.55)" }}>
              Message *
            </label>
            <textarea
              name="message"
              data-testid="contact-message"
              value={form.message}
              onChange={onChange}
              rows={6}
              required
              className="w-full bg-transparent border border-white/15 focus:border-[color:var(--csg-accent)] outline-none p-4 font-body text-base text-white placeholder:text-white/30 resize-none transition-colors"
              placeholder="Tell us what you'd like to discuss…"
            />
          </div>

          <div className="flex justify-center pt-2">
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="csg-btn disabled:opacity-50"
              style={{ backgroundColor: dept.accent }}
            >
              {sending ? "Sending…" : "Send Message"} <Send size={14} />
            </button>
          </div>
        </form>

        {dept.instagram && (
          <div className="mt-14 text-center">
            <a
              href={dept.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="inline-flex items-center gap-3 border border-white/15 px-6 py-3.5 text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-white transition-colors"
              style={{ borderColor: "rgba(255,255,255,0.15)" }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = dept.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)")}
            >
              <Instagram size={16} style={{ color: dept.accent }} />
              Follow us on Instagram
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ name, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="csg-label text-white/60 block mb-2" style={{ color: "rgba(255,255,255,0.55)" }}>
        {label} {required && "*"}
      </label>
      <input
        type={type}
        name={name}
        data-testid={`contact-${name}`}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border border-white/15 focus:border-[color:var(--csg-accent)] outline-none p-4 font-body text-base text-white placeholder:text-white/30 transition-colors"
      />
    </div>
  );
}
